import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { loadData, deleteData, updateData, addData } from "../../../../config/data";
import EmptyAsset from "../emptyAsset";
import Lang from "../../../../config/lang";
import { useProfileTab } from "../../ProfileTabContext";

const LicenseCertificationForm = ({ setIsFading, updatePercentage, profileCompletion }) => {
    const { lang } = useSelector((state) => state.globalReducer);
    const { currentTabIndex, setCurrentTabIndex } = useProfileTab();

    const [isEdit, setIsEdit] = useState(false);
    const [certifications, setCertifications] = useState([]);
    const [isLoading, setisLoading] = useState('');

    const applicantID = localStorage?.getItem("applicantID");
    const userToken = localStorage?.getItem('authToken') || '';

    const loadCertifications = async () => {
        loadData({
            url: `ApplicantCertifications`,
            params: [{ title: "filter", value: `applicant:${applicantID}` }],
            userToken,
        }).then((res) => {
            if (res?.data) {
                setCertifications(res.data);
            }
        });
    };

    useEffect(() => {
        loadCertifications();
    }, []);

    const handleChange = (index, field, value) => {
        const updated = [...certifications];
        updated[index][field] = value;
        setCertifications(updated);
    };

    const handleFileChange = (index, file) => {
        const updated = [...certifications];
        updated[index].file = file;
        setCertifications(updated);
    };

    const handleAdd = () => {
        setIsEdit(true)
        setCertifications([
            ...certifications,
            {
                certificationID: null,
                applicantID: applicantID,
                certificateName: "",
                issuingOrganization: "",
                credentialID: "",
                issueDate: "",
                expirationDate: "",
                credentialURL: "",
            },
        ]);
    };

    const handleDelete = (cert) => {
        if (cert.certificationID) {
            deleteData({
                url: `ApplicantCertifications`,
                id: cert.certificationID,
                userToken
            }).then(() => {
                loadCertifications();
            });
        } else {
            setCertifications(certifications.filter((c) => c !== cert));
        }
    };

    const saveData = async (cert) => {
        try {
            if (cert.certificationID) {
                await updateData({
                    url: "ApplicantCertifications",
                    userToken,
                    reqBody: {
                        certificationID: cert.certificationID,
                        applicantID: applicantID,
                        certificateName: cert.certificateName,
                        issuingOrganization: cert.issuingOrganization,
                        credentialID: cert.credentialID,
                        issueDate: cert.issueDate,
                        expirationDate: cert.expirationDate,
                        credentialURL: cert.credentialURL,
                    }
                });
            } else {
                await addData({
                    url: "ApplicantCertifications",
                    userToken,
                    reqBody: {
                        applicantID: applicantID,
                        certificateName: cert.certificateName,
                        issuingOrganization: cert.issuingOrganization,
                        credentialID: cert.credentialID,
                        issueDate: cert.issueDate,
                        expirationDate: cert.expirationDate,
                        credentialURL: cert.credentialURL,
                    },
                });
            }
        } catch (err) {
            console.error("Failed to save organization:", err);
        }
    };

    const handleSave = async () => {
        setisLoading(true);
        for (let cert of certifications) {
            await saveData(cert);
        }
        setIsEdit(false);
        loadCertifications();
        setisLoading(false)
    };

    const isFormReady = () => {
        const hasEmptyId = certifications.some(item => !item.certificateName || !item.issuingOrganization || !item.issueDate);
        return !hasEmptyId;
    }

    const safeDate = (value) => {
        if (!value) return "";
        return value.split("T")[0];
    };

    return (
        <div>

            <div className="mt-4 bg-white p-6 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.05)]" style={{ minHeight: window.innerHeight - 300 }}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-blue-700 font-medium md:text-[20px] text-[17px] flex items-center space-x-2 cursor-pointer">
                        <img src={"/asset/icon/license_blue.svg"} className="w-[16px]" />
                        <span>{Lang(lang, 'license_certifcate')}</span>
                    </h2>
                    {!isEdit && (
                        <button
                            onClick={() => {
                                setIsEdit(true)
                            }}
                            className="flex border border-gray-300 rounded-full px-5 py-2 text-sm text-white bg-red-500 hover:bg-red-600"
                        >
                            <img src={"/asset/icon/edit.svg"} className="w-[16px] mr-1" />
                            Edit
                        </button>
                    )}
                </div>

                <form className="space-y-6">
                    {certifications.length === 0 ?
                        <div className="w-full">
                            <EmptyAsset title={Lang(lang, 'no_license')} desc={Lang(lang, 'no_license_desc')} />
                            {isEdit && <button
                                type="button"
                                onClick={handleAdd}
                                className="w-full text-blue-600 border border-dashed border-blue-300 rounded-full py-2 text-sm hover:bg-blue-50 mb-4"
                            >
                                + {Lang(lang, 'add_license')}
                            </button>}
                        </div> : certifications.map((cert, index) => (
                            <div
                                key={cert?.certificationID ?? `temp-${index}`}
                                className="border border-gray-200 rounded-xl p-4 relative"
                            >
                                {isEdit && (
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(cert)}
                                        className="absolute top-3 right-3 text-red-500 hover:text-red-700"
                                    >
                                        <img src={"/asset/icon/trash.svg"} className="w-[16px]" />
                                    </button>
                                )}

                                <h3 className="text-gray-700 font-bold mb-3 text-[#1E40AF]">
                                    {Lang(lang, 'certification')} {index + 1}
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <label className="block font-medium mb-1">
                                            {Lang(lang, 'certificate_name')}<span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={cert.certificateName}
                                            onChange={(e) =>
                                                handleChange(index, "certificateName", e.target.value)
                                            }
                                            className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                                            placeholder={Lang(lang, 'certificate_name')}
                                            disabled={!isEdit}
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-medium mb-1">
                                            {Lang(lang, 'issuing_org')}<span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={cert.issuingOrganization}
                                            onChange={(e) =>
                                                handleChange(index, "issuingOrganization", e.target.value)
                                            }
                                            className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                                            placeholder={Lang(lang, 'organization')}
                                            disabled={!isEdit}
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-medium mb-1">Credential ID</label>
                                        <input
                                            type="text"
                                            value={cert.credentialID}
                                            onChange={(e) =>
                                                handleChange(index, "credentialID", e.target.value)
                                            }
                                            className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                                            placeholder="Credential ID"
                                            disabled={!isEdit}
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-medium mb-1">{Lang(lang, 'issue_date')}<span className="text-red-500">*</span></label>
                                        <input
                                            type="date"
                                            value={`${safeDate(cert.issueDate)}`.split("T")[0].slice(0, 10)}
                                            onChange={(e) =>
                                                handleChange(index, "issueDate", e.target.value)
                                            }
                                            className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                                            disabled={!isEdit}
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-medium mb-1">{Lang(lang, 'expiration_date')}</label>
                                        <input
                                            type="date"
                                            value={`${safeDate(cert.expirationDate)}`.split("T")[0].slice(0, 10)}
                                            onChange={(e) =>
                                                handleChange(index, "expirationDate", e.target.value)
                                            }
                                            className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                                            disabled={!isEdit}
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-medium mb-1">{Lang(lang, 'Credential_uRL')}</label>
                                        <input
                                            type="url"
                                            value={cert.credentialURL}
                                            onChange={(e) => handleChange(index, "credentialURL", e.target.value)}
                                            className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                                            placeholder="https://..."
                                            disabled={!isEdit}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        {cert.file && (
                                            <div className="flex items-center justify-between mt-3 p-2 border rounded-lg bg-gray-50 text-xs">
                                                <div className="flex items-center space-x-2">
                                                    <img
                                                        src="/asset/icon/upload.svg"
                                                        alt="file"
                                                        className="w-4 h-4"
                                                    />
                                                    <span className="text-gray-700">{cert.file.name}</span>
                                                    <span className="text-gray-400">
                                                        ({(cert.file.size / 1024 / 1024).toFixed(1)} MB)
                                                    </span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-green-500">✔</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleFileChange(index, null)}
                                                        className="text-gray-400 hover:text-red-500"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                    {isEdit && certifications.length !== 0 && (
                        <button
                            type="button"
                            onClick={handleAdd}
                            className="w-full text-blue-600 border border-dashed border-blue-300 rounded-full py-2 text-sm hover:bg-blue-50 mb-4"
                        >
                            + {Lang(lang, 'add_license')}
                        </button>
                    )}
                </form>
                {!(isFormReady()) && <div className="flex justify-end w-full mt-3">
                    <p className="text-red-500 text-xs mt-1 mr-0">{Lang(lang, 'required_error')}</p>
                </div>}


            </div>

            {isEdit ? (
                <div className="flex space-x-2 mt-3 justify-end w-full bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] p-3 rounded-xl sticky bottom-3">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-full px-4 py-1 text-sm hover:bg-gray-50"
                        onClick={() => {
                            setIsFading(true);
                            setTimeout(() => {
                                setCurrentTabIndex(currentTabIndex - 1);
                                setIsFading(false);
                            }, 200);
                        }}
                    >
                        {Lang(lang, 'previous')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (isFormReady() && !isLoading) {
                                handleSave()
                                setIsFading(true);
                                setTimeout(() => {
                                    if ((currentTabIndex + 1) > profileCompletion) {
                                        updatePercentage(currentTabIndex + 1)
                                    }
                                    setCurrentTabIndex(currentTabIndex + 1);
                                    setIsFading(false);
                                }, 200);
                            }

                        }}
                        className={(isFormReady() ? "bg-red-500 hover:bg-red-600 text-white" : "border border-gray-300 hover:bg-gray-50") + " rounded-full px-4 py-2 text-sm"}
                    >
                        {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : Lang(lang, 'save_and_next')}
                    </button>
                </div>
            ) : <div className="flex space-x-2 mt-3 justify-end w-full bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] p-3 rounded-xl sticky bottom-3">
                <button
                    type="button"
                    className="border border-gray-300 rounded-full px-4 py-1 text-sm hover:bg-gray-50"
                    onClick={() => {
                        setIsFading(true);
                        setTimeout(() => {
                            setCurrentTabIndex(currentTabIndex - 1);
                            setIsFading(false);
                        }, 200);
                    }}
                >
                    {Lang(lang, 'previous')}
                </button>
                <button
                    type="submit"
                    className={
                        "rounded-full px-4 py-1 text-sm " +
                        (isEdit
                            ? "text-white bg-red-500 hover:bg-red-600"
                            : "border border-red-500 text-red-500 hover:bg-gray-50")
                    }
                    onClick={() => {
                        if (isFormReady()) {

                            setIsFading(true);
                            setTimeout(() => {
                                if ((currentTabIndex + 1) > profileCompletion) {
                                    updatePercentage(currentTabIndex + 1)
                                }
                                setCurrentTabIndex(currentTabIndex + 1);
                                setIsFading(false);
                            }, 200);
                        }
                    }}
                >
                    {Lang(lang, 'next')} {currentTabIndex + 1}/10
                </button>
            </div>}
        </div>
    );
};

export default LicenseCertificationForm;
